package domain.player

import androidx.compose.ui.input.key.Key
import kotlinx.serialization.Serializable

@Serializable
enum class GameAction(val displayName: String) {
    MOVE_LEFT("MOVE FALLING PIECE LEFT"),
    MOVE_RIGHT("MOVE FALLING PIECE RIGHT"),
    SOFT_DROP("SOFT DROP"),
    HARD_DROP("HARD DROP"),
    ROTATE_CCW("ROTATE COUNTERCLOCKWISE"),
    ROTATE_CW("ROTATE CLOCKWISE"),
    ROTATE_180("ROTATE 180"),
    HOLD("SWAP HOLD PIECE"),
    FORFEIT("FORFEIT GAME"),
    RETRY("RETRY GAME"),
}

data class KeyBinding(
    val action: GameAction,
    val keys: List<String>,
)

data class KeyBindings(val bindings: Map<GameAction, List<String>>)

object KeyBindingStore {

    val guidelineBindings: Map<GameAction, List<String>> = mapOf(
        GameAction.MOVE_LEFT to listOf(Key.DirectionLeft.keyCode.toString()),
        GameAction.MOVE_RIGHT to listOf(Key.DirectionRight.keyCode.toString()),
        GameAction.SOFT_DROP to listOf(Key.DirectionDown.keyCode.toString()),
        GameAction.HARD_DROP to listOf(Key.Spacebar.keyCode.toString()),
        GameAction.ROTATE_CCW to listOf(Key.Z.keyCode.toString()),
        GameAction.ROTATE_CW to listOf(Key.X.keyCode.toString(), Key.DirectionUp.keyCode.toString()),
        GameAction.ROTATE_180 to listOf(Key.A.keyCode.toString()),
        GameAction.HOLD to listOf(Key.C.keyCode.toString(), Key.ShiftLeft.keyCode.toString(), Key.ShiftRight.keyCode.toString()),
        GameAction.FORFEIT to listOf(Key.Escape.keyCode.toString()),
        GameAction.RETRY to listOf(Key.R.keyCode.toString()),
    )

    val wasdBindings: Map<GameAction, List<String>> = mapOf(
        GameAction.MOVE_LEFT to listOf(Key.A.keyCode.toString()),
        GameAction.MOVE_RIGHT to listOf(Key.D.keyCode.toString()),
        GameAction.SOFT_DROP to listOf(Key.S.keyCode.toString()),
        GameAction.HARD_DROP to listOf(Key.W.keyCode.toString()),
        GameAction.ROTATE_CCW to listOf(Key.DirectionLeft.keyCode.toString()),
        GameAction.ROTATE_CW to listOf(Key.DirectionRight.keyCode.toString()),
        GameAction.ROTATE_180 to listOf(Key.DirectionUp.keyCode.toString()),
        GameAction.HOLD to listOf(Key.ShiftLeft.keyCode.toString()),
        GameAction.FORFEIT to listOf(Key.Escape.keyCode.toString()),
        GameAction.RETRY to listOf(Key.R.keyCode.toString()),
    )

    /** Load custom bindings from the player profile. */
    fun loadCustom(): Map<GameAction, List<String>> {
        val fromProfile = PlayerSession.keyBindings
        if (fromProfile.isNotEmpty()) return fromProfile
        return emptyMap()
    }

    fun saveCustom(bindings: Map<GameAction, List<String>>) {
        PlayerSession.updateKeyBindings(bindings)
    }

    fun resetCustom(): Map<GameAction, List<String>> {
        PlayerSession.updateKeyBindings(emptyMap<GameAction, List<String>>())
        return guidelineBindings.mapValues { it.value.toList() }
    }

    /**
     * Returns the active bindings to use in-game.
     * Falls back to guideline defaults for any unmapped action.
     */
    fun loadActive(): Map<GameAction, List<String>> {
        val custom = loadCustom()
        return if (custom.isEmpty()) guidelineBindings
        else GameAction.entries.associateWith { action ->
            custom[action]?.takeIf { it.isNotEmpty() } ?: guidelineBindings[action] ?: emptyList()
        }
    }

    fun formatKeyCode(code: String): String = when (code) {
        Key.DirectionLeft.keyCode.toString() -> "LEFT"
        Key.DirectionRight.keyCode.toString() -> "RIGHT"
        Key.DirectionUp.keyCode.toString() -> "UP"
        Key.DirectionDown.keyCode.toString() -> "DOWN"
        Key.Spacebar.keyCode.toString() -> "SPACE"
        Key.ShiftLeft.keyCode.toString() -> "L-SHIFT"
        Key.ShiftRight.keyCode.toString() -> "R-SHIFT"
        Key.CtrlLeft.keyCode.toString() -> "L-CTRL"
        Key.CtrlRight.keyCode.toString() -> "R-CTRL"
        Key.AltLeft.keyCode.toString() -> "L-ALT"
        Key.AltRight.keyCode.toString() -> "R-ALT"
        Key.Enter.keyCode.toString() -> "ENTER"
        Key.Backspace.keyCode.toString() -> "BKSP"
        Key.Tab.keyCode.toString() -> "TAB"
        Key.Escape.keyCode.toString() -> "ESC"
        Key.CapsLock.keyCode.toString() -> "CAPS"
        Key.A.keyCode.toString() -> "A"
        Key.B.keyCode.toString() -> "B"
        Key.C.keyCode.toString() -> "C"
        Key.D.keyCode.toString() -> "D"
        Key.E.keyCode.toString() -> "E"
        Key.F.keyCode.toString() -> "F"
        Key.G.keyCode.toString() -> "G"
        Key.H.keyCode.toString() -> "H"
        Key.I.keyCode.toString() -> "I"
        Key.J.keyCode.toString() -> "J"
        Key.K.keyCode.toString() -> "K"
        Key.L.keyCode.toString() -> "L"
        Key.M.keyCode.toString() -> "M"
        Key.N.keyCode.toString() -> "N"
        Key.O.keyCode.toString() -> "O"
        Key.P.keyCode.toString() -> "P"
        Key.Q.keyCode.toString() -> "Q"
        Key.R.keyCode.toString() -> "R"
        Key.S.keyCode.toString() -> "S"
        Key.T.keyCode.toString() -> "T"
        Key.U.keyCode.toString() -> "U"
        Key.V.keyCode.toString() -> "V"
        Key.W.keyCode.toString() -> "W"
        Key.X.keyCode.toString() -> "X"
        Key.Y.keyCode.toString() -> "Y"
        Key.Z.keyCode.toString() -> "Z"
        Key.Zero.keyCode.toString() -> "0"
        Key.One.keyCode.toString() -> "1"
        Key.Two.keyCode.toString() -> "2"
        Key.Three.keyCode.toString() -> "3"
        Key.Four.keyCode.toString() -> "4"
        Key.Five.keyCode.toString() -> "5"
        Key.Six.keyCode.toString() -> "6"
        Key.Seven.keyCode.toString() -> "7"
        Key.Eight.keyCode.toString() -> "8"
        Key.Nine.keyCode.toString() -> "9"
        Key.NumPad0.keyCode.toString() -> "NUM0"
        Key.NumPad1.keyCode.toString() -> "NUM1"
        Key.NumPad2.keyCode.toString() -> "NUM2"
        Key.NumPad3.keyCode.toString() -> "NUM3"
        Key.NumPad4.keyCode.toString() -> "NUM4"
        Key.NumPad5.keyCode.toString() -> "NUM5"
        Key.NumPad6.keyCode.toString() -> "NUM6"
        Key.NumPad7.keyCode.toString() -> "NUM7"
        Key.NumPad8.keyCode.toString() -> "NUM8"
        Key.NumPad9.keyCode.toString() -> "NUM9"
        else -> code.take(6).uppercase()
    }
}
